import {ExclamationCircleFilled, MoonOutlined, SunOutlined} from '@ant-design/icons';
import {Button, Flex, theme, Typography} from 'antd';
import {Loading, SnackBar, Wrapper} from 'Components';
import {useThemeMode} from 'Context/ThemeModeContext';
import useSnackBar from 'Hooks/SnackBarErrorHook';
import {agentLogin} from 'Redux/Slices/login';
import {SEARCH} from 'Utils/constants/Paths';
import {SSO_ERROR_TEXT, SSO_LOADING_TEXT} from 'Utils/constants/genericConstants';
import React, {useEffect, useState} from 'react';
import {useDispatch} from 'react-redux';
import {useLocation, useNavigate, useSearchParams} from 'react-router-dom';
import styles from './styles';

const {Title} = Typography;

interface CustomizedState {
  from: {
    pathname: string;
  };
}
const SSOLogin = () => {
  const [searchParams] = useSearchParams();
  const [UIMessage, setUIMessage] = useState(SSO_LOADING_TEXT);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [isSnackBarOpen, closeSnackBar] = useSnackBar(error);
  const location = useLocation();
  const state = location.state as CustomizedState;
  const from = state?.from?.pathname || SEARCH;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {token: themeToken} = theme.useToken();
  const {mode, toggleMode} = useThemeMode();

  useEffect(() => {
    /*
    The token is base64 format and when fetched from the url the '+' gets turned into whitespace.
    We revert this to have a valid base64 token

    SSO Login with SAML Token
    */
    const token = searchParams?.get('SAMLResponse')?.replace(/\s+/g, '+');

    if (token) {
      (async () => {
        const resp = await dispatch(agentLogin({token})).unwrap();
        if (resp.data) {
          navigate(from, {replace: true});
        } else if (resp.error) {
          const {response: errorResponse} = resp.error;
          if (errorResponse?.status === 401) {
            setError(SSO_ERROR_TEXT[errorResponse?.status]);
            setUIMessage(SSO_ERROR_TEXT[errorResponse?.status]);
          } else {
            setUIMessage(SSO_ERROR_TEXT.default);
            setError(errorResponse?.data?.message || 'Something went wrong');
          }
        }

        setLoading(false);
      })();
    } else {
      setLoading(false);
      setUIMessage('Missing token');
    }
  }, []);

  return (
    <Wrapper>
      <Button
        type="text"
        shape="circle"
        style={styles.themeToggle}
        aria-label={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
        icon={mode === 'dark' ? <SunOutlined /> : <MoonOutlined />}
        onClick={toggleMode}
      />
      <Flex vertical align="center" style={styles.container}>
        {loading && <Loading />}
        {!loading && (
          <Flex justify="center" align="center" style={styles.errorIconBox}>
            <ExclamationCircleFilled style={{fontSize: 50, color: themeToken.colorPrimary}} />
          </Flex>
        )}
        <Title level={5} style={{margin: 0}}>
          {UIMessage}
        </Title>
      </Flex>
      <SnackBar
        errorMessage={error}
        isSnackBarOpen={isSnackBarOpen}
        closeSnackBar={closeSnackBar}
      />
    </Wrapper>
  );
};

export default SSOLogin;
